/**
 * StrandShowcase — the canonical "strand standing on a stage" component.
 *
 * Same markup, same sprite resolution, same swap behavior as the home-page
 * party showcase. Use this everywhere a full-figure strand needs to appear
 * on a hub-style stage (home, workshop, future hub screens). Other screens
 * keep getting this wrong when they reimplement it; this is the one place
 * that owns the contract.
 *
 * The CSS lives under .party-member / .party-portrait-wrap / .party-info in
 * main-menu.css — keep using those classes so the look stays identical.
 *
 * Caller responsibility:
 *   - When swappable, provide onSwap() — typically opens RosterPicker. The
 *     filtering / pick logic varies per screen, so this component only
 *     surfaces the click; the caller decides what gets swapped to what.
 */

import { renderUnitPortrait } from './UnitPortrait';
import { getElementIconUrl } from './ItemIcon';
import { Tooltip } from './Tooltip';
import { buildElementTooltip } from './ElementTooltip';
import { PLAYER_SPRITES } from '../../combat/spriteConfig';
import type { PartyUnit, ConfigData } from '../../types';

export interface StrandShowcaseOptions {
  unit: PartyUnit;
  configData: ConfigData | undefined;
  /** Show the swap pill below the sprite and make the card tappable. */
  swappable?: boolean;
  /** Optional unspent-mastery / notification dot. */
  hasAlert?: boolean;
  /** Extra classes added to the .party-member root (e.g. screen-specific tweaks). */
  extraClasses?: string;
  /** Override the swap pill label. Defaults to "⇄ Swap". */
  swapLabel?: string;
  /** Optional raw HTML rendered above the sprite (e.g. equip chips, mastery pills). */
  aboveHead?: string;
}

/** Resolve the sprite key the same way every other screen does. Used here
 *  to keep skin/template/class fallback consistent. Final fallback is
 *  'warrior' — a known-good sprite — so a missing template never produces
 *  an empty img src that collapses the layout. */
export function resolveSpriteKey(unit: PartyUnit, configData: ConfigData | undefined): string {
  const template = configData?.unitTemplates?.[unit.unitClass];
  const skinKey = unit.activeSkinId && configData?.skins?.[unit.activeSkinId]?.spriteKey;
  return skinKey || template?.spriteKey || 'warrior';
}

/** Build the strand-showcase HTML. Returns a single .party-member element. */
export function renderStrandShowcase(options: StrandShowcaseOptions): string {
  const { unit, configData, swappable = false, hasAlert = false, extraClasses = '', swapLabel, aboveHead } = options;
  const spriteKey = resolveSpriteKey(unit, configData);
  const types = (unit.defensiveTypes && unit.defensiveTypes.length > 0)
    ? unit.defensiveTypes
    : (unit.element ? [unit.element] : []);
  const typeIcons = types
    .map(t => `<img class="party-type-icon" src="${getElementIconUrl(t)}" alt="${t}" width="16" height="16" draggable="false" data-element="${t}">`)
    .join('');
  const tappableCls = swappable ? ' party-member--tappable' : '';
  const allExtra = extraClasses ? ` ${extraClasses}` : '';
  const label = swapLabel ?? '⇄ Swap';

  return `
    <div class="party-member${tappableCls}${allExtra}" data-unit-id="${unit.id}">
      ${aboveHead ? `<div class="party-above-head">${aboveHead}</div>` : ''}
      <div class="party-portrait-wrap">
        ${renderUnitPortrait({ spriteKey, name: unit.name, size: 'lg', scale: PLAYER_SPRITES[spriteKey]?.scale })}
        ${hasAlert ? '<span class="party-member-dot" aria-label="Unspent mastery points"></span>' : ''}
        ${swappable ? `<div class="party-swap-label">${label}</div>` : ''}
      </div>
      <div class="party-info">
        <span class="party-name">${unit.name}</span>
        <span class="unit-level">Lv.${unit.level ?? 1}</span>
        ${typeIcons ? `<span class="party-types">${typeIcons}</span>` : ''}
      </div>
    </div>
  `;
}

/** Wire interactivity on a rendered .party-member element.
 *  - Click → onSwap (only when swappable)
 *  - Repositions the swap label to align with the sprite's actual bottom edge,
 *    since sprite bounding boxes vary per race/class. */
export function bindStrandShowcase(
  memberEl: HTMLElement,
  opts: { onSwap?: () => void } = {},
): void {
  const { onSwap } = opts;
  const isTappable = memberEl.classList.contains('party-member--tappable');

  if (isTappable && onSwap) {
    memberEl.addEventListener('click', () => onSwap());
  }

  memberEl.querySelectorAll('.party-type-icon').forEach(iconEl => {
    const element = (iconEl as HTMLElement).dataset.element;
    if (element) Tooltip.attach(iconEl as HTMLElement, () => buildElementTooltip(element));
  });

  const img = memberEl.querySelector('.unit-portrait') as HTMLImageElement | null;
  const label = memberEl.querySelector('.party-swap-label') as HTMLElement | null;
  const wrap = memberEl.querySelector('.party-portrait-wrap') as HTMLElement | null;

  if (img && label && wrap) {
    const positionLabel = () => {
      const wrapRect = wrap.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      if (wrapRect.height && imgRect.height) {
        const offset = wrapRect.bottom - imgRect.top + 8;
        label.style.bottom = `${offset}px`;
      }
    };
    if (img.complete) positionLabel();
    else img.addEventListener('load', positionLabel);
  }
}
